import { User } from '../types/auth';
import { hasValidSecurityTrial, getTrialStatusMessage } from './trialUtils';

export interface TrialUIState {
  showTrialBadge: boolean;
  showTrialWarning: boolean;
  trialMessage: string | null;
  accessLevel: 'full' | 'limited' | 'blocked';
  creditsRemaining: number;
  isTrialUser: boolean;
}

/**
 * Get trial UI state for displaying trial-related elements
 */
export const getTrialUIState = (user: User | null): TrialUIState => {
  if (!user) {
    return {
      showTrialBadge: false,
      showTrialWarning: false,
      trialMessage: null,
      accessLevel: 'blocked',
      creditsRemaining: 0,
      isTrialUser: false
    };
  }

  const hasAccess = hasValidSecurityTrial(user);
  const trialMessage = getTrialStatusMessage(user);
  const creditsRemaining = user.remaining_credits || 0;
  
  // Determine if user is in trial mode (not super admin and using credit system)
  const isTrialUser = user.role !== 'super_admin' && Boolean(user.use_credit_system);
  
  // Show trial badge only for actual trials, not credit system users
  const showTrialBadge = Boolean(user.security_trial) || 
    Boolean(user.web_trial) || 
    Boolean(user.ide_trial) || 
    Boolean(user.devops_trial);

  // Show warning if credits are low or trial is expiring
  const showTrialWarning = Boolean(isTrialUser && (
    creditsRemaining <= 10 || // Low credits warning
    !hasAccess || // No access
    (Boolean(user.security_trial) && user.security_trial_expires && 
     new Date(user.security_trial_expires).getTime() <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).getTime()) // Expires in 7 days
  ));

  // Determine access level
  let accessLevel: 'full' | 'limited' | 'blocked' = 'blocked';
  if (user.role === 'super_admin') {
    accessLevel = 'full';
  } else if (hasAccess) {
    accessLevel = creditsRemaining > 50 ? 'full' : 'limited';
  }

  return {
    showTrialBadge,
    showTrialWarning,
    trialMessage,
    accessLevel,
    creditsRemaining,
    isTrialUser
  };
};

/**
 * Get trial badge text for UI display
 */
export const getTrialBadgeText = (user: User | null): string => {
  if (!user) return '';

  if (user.role === 'super_admin') return '';

  const now = new Date();

  if (user.security_trial && user.security_trial_expires) {
    const expiryDate = new Date(user.security_trial_expires);

    if (expiryDate < now) {
      return 'Trial expired';
    }

    const formattedDate = expiryDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    return `Trial active till ${formattedDate}`;
  }

  if (user.security_trial) return 'Security Trial';

  return '';
};


/**
 * Get trial badge color/variant for UI styling
 */
export const getTrialBadgeVariant = (
  user: User | null
): 'success' | 'warning' | 'error' | 'info' => {
  if (!user) return 'error';

  if (user.role === 'super_admin') return 'success';

  const credits = user.remaining_credits || 0;

  // Explicitly check expiration
  if (!user.security_trial || !user.security_trial_expires) {
    return 'error';
  }

  const expiryDate = new Date(user.security_trial_expires);
  const now = new Date();

  if (expiryDate < now) {
    return 'error'; // Trial has expired
  }

  if (credits <= 10) return 'warning';
  if (credits <= 50) return 'info';

  return 'success';
};

/**
 * Check if user should see upgrade prompts
 */
export const shouldShowUpgradePrompt = (user: User | null): boolean => {
  if (!user || user.role === 'super_admin') return false;
  
  const credits = user.remaining_credits || 0;
  const hasActiveTrials = user.security_trial;
  
  // Show upgrade prompt if:
  // - Credits are low (<=20)
  // - No active trials and no credits
  // - Trial is expiring soon
  return credits <= 20 || (!hasActiveTrials && credits === 0);
};

/**
 * Get contextual message for trial status
 */
export const getTrialContextMessage = (user: User | null): string | null => {
  if (!user || user.role === 'super_admin') return null;
  
  const credits = user.remaining_credits || 0;
  const hasAccess = hasValidSecurityTrial(user);
  
  if (!hasAccess) {
    return 'Your trial has expired. Upgrade to continue using security features.';
  }
  
  if (credits <= 5) {
    return `Only ${credits} credits remaining. Consider upgrading to avoid service interruption.`;
  }
  
  if (credits <= 20) {
    return `${credits} credits remaining. Plan your usage accordingly.`;
  }
  
  return null;
};

/**
 * Format credits display for UI
 */
export const formatCreditsDisplay = (credits: number): string => {
  if (credits === 0) return 'No credits';
  if (credits === 1) return '1 credit';
  return `${credits} credits`;
};
